import {
  ComponentPropsWithRef,
  forwardRef,
  ForwardRefRenderFunction,
  useState,
  useRef,
  useCallback,
} from 'react';
import styles from './style.module.css';
interface IInput extends ComponentPropsWithRef<'input'> {
  placeholder?: string;
  name: string;
  type: string;
  icon?: any;
  style?: any;
  errors?: string;
  label?: string | JSX.Element;
  prefix?: string | undefined;
}

const hasNativePasswordReveal =
  navigator.userAgent.includes('Edg/') ||
  navigator.userAgent.includes('Firefox/');

export const InputBase: ForwardRefRenderFunction<HTMLInputElement, IInput> = (
  { name, type, label, prefix, placeholder, errors, onChange, ...rest },
  ref
) => {
  const isPassword = type === 'password';
  const [showPassword, setShowPassword] = useState(false);
  const [peekActive, setPeekActive] = useState(false);
  const [peekChar, setPeekChar] = useState('');
  const [inputLength, setInputLength] = useState(0);
  const peekTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isPassword) {
        const newValue = e.target.value;
        const isAdding = newValue.length > inputLength;

        setInputLength(newValue.length);

        if (isAdding) {
          const lastChar = newValue[newValue.length - 1];
          setPeekChar(lastChar);
          setPeekActive(true);

          if (peekTimeoutRef.current) clearTimeout(peekTimeoutRef.current);
          peekTimeoutRef.current = setTimeout(() => {
            setPeekActive(false);
          }, 1000);
        } else {
          setPeekActive(false);
          if (peekTimeoutRef.current) clearTimeout(peekTimeoutRef.current);
        }
      }

      onChange?.(e);
    },
    [isPassword, inputLength, onChange]
  );

  const showOverlay = isPassword && peekActive && !showPassword;
  const overlayText = '•'.repeat(Math.max(0, inputLength - 1)) + peekChar;
  const actualType = isPassword && showPassword ? 'text' : type;

  return (
    <>
      {label && <label htmlFor={name}>{label}</label>}
      <div
        className={`d-flex align-items-center ${isPassword ? styles.passwordWrapper : ''}`}
      >
        {prefix && <span className=" p-2">{prefix}</span>}
        <input
          className={`${styles.input} ${isPassword ? styles.passwordInput : ''} ${showOverlay ? styles.inputTransparent : ''}`}
          type={actualType}
          placeholder={placeholder}
          name={name}
          onChange={isPassword ? handleChange : onChange}
          {...rest}
          ref={ref}
        />
        {showOverlay && (
          <div className={styles.peekOverlay} aria-hidden="true">
            {overlayText}
          </div>
        )}
        {isPassword && !hasNativePasswordReveal && (
          <button
            type="button"
            className={styles.eyeButton}
            onClick={() => setShowPassword((prev) => !prev)}
            tabIndex={-1}
            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
          >
            <i className="material-icons-outlined">
              {showPassword ? 'visibility_off' : 'visibility'}
            </i>
          </button>
        )}
      </div>

      <span className={styles.error}>{errors}</span>
    </>
  );
};

export const Input = forwardRef(InputBase);
